import React, { useEffect, useMemo, useState, useCallback, memo } from "react";
import styles from "@components/OrderController/Modals/condimentsModal.module.scss";
import cn from "classnames";
import FullWidthModal from "@components/common/Modal/FullWidthModal";
import ModalHeader from "@components/common/ModalHeader/index";
import { TouchableButton, TouchableDiv } from "@components/common/Touchable";
import { LABEL } from "@veriables";
import { safeJsonParse } from "@utility/helperFunction";
import Loader from "@components/common/Loader/newLoaderModal";

const TrashIcon = memo(() => (
  <span className="p-0">
    <i className="fas fa-trash-alt fs-12 color-white d-flex justify-content-center align-items-center" />
  </span>
));

const MinusIcon = memo(() => (
  <span className="p-0">
    <i className="fas fa-minus fs-12 color-white d-flex justify-content-center align-items-center" />
  </span>
));

const PlusIcon = memo(() => (
  <i className="fas fa-plus fs-12 d-flex justify-content-center align-items-center color-white" role="presentation" />
));

const outOfStockWrapStyle = { display: "flex", justifyContent: "flex-end" };

const CondimentTile = memo(({
  tileKey,
  item,
  itemDetails,
  quantity,
  serverAppTheme,
  onTileClick,
  onIncrement,
  onDecrement,
}) => {
  const serverAppImage =
    itemDetails?.serverAppAsImage && itemDetails?.intermediateImageUrl
      ? `url(${itemDetails?.intermediateImageUrl})`
      : "";

  const tileStyle = useMemo(() => ({
    width: "15%",
    height: "61px",
    backgroundImage: serverAppImage,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% 100%",
    backgroundColor: itemDetails?.serverAppButtonColor || "",
  }), [serverAppImage, itemDetails?.serverAppButtonColor]);

  const nameStyle = useMemo(() => ({
    color: itemDetails?.serverAppButtonFontColor || serverAppTheme?.primary_text,
  }), [itemDetails?.serverAppButtonFontColor, serverAppTheme?.primary_text]);

  const decrementBtnStyle = useMemo(() => ({
    borderRadius: "0.1rem",
    backgroundColor: serverAppTheme?.primaryBtn,
  }), [serverAppTheme?.primaryBtn]);

  const incrementBtnStyle = useMemo(() => ({
    backgroundColor: serverAppTheme?.primaryBtn,
  }), [serverAppTheme?.primaryBtn]);

  const showName = !(itemDetails?.serverAppAsImage && itemDetails?.intermediateImageUrl);

  const handleTileClick = useCallback((e) => {
    e.preventDefault();
    onTileClick(tileKey, item, itemDetails);
  }, [tileKey, item, itemDetails, onTileClick]);

  const handleIncrement = useCallback((e) => {
    e.stopPropagation();
    onIncrement(tileKey, item, itemDetails);
  }, [tileKey, item, itemDetails, onIncrement]);

  const handleDecrement = useCallback((e) => {
    e.stopPropagation();
    onDecrement(tileKey);
  }, [tileKey, onDecrement]);

  return (
    <TouchableDiv
      className={cn(
        quantity > 0 ? styles.selectedItem : styles.defaultItem,
        "p-1",
        styles.itemPadding,
        "d-flex flex-column justify-content-between"
      )}
      style={tileStyle}
      onClick={itemDetails?.outOfStock ? undefined : handleTileClick}
    >
      <div
        className={cn(
          "fw-500",
          styles.displayNameFont,
          quantity > 0 ? styles.ellipsis : ""
        )}
        style={nameStyle}
      >
        {showName ? itemDetails?.name : ""}
      </div>

      {itemDetails?.outOfStock ? (
        <div style={outOfStockWrapStyle}>
          <div className={cn(styles.outOfStockNumber, "fs-12 rounded fw-600 d-flex justify-content-center align-items-center", styles.outOfStockStyle)}>
            {LABEL().OUT_OF_STOCK_NUMBER}
          </div>
        </div>
      ) : quantity > 0 ? (
        <div className={cn("d-flex mx-1 mb-1", itemDetails?.name?.length > 20 ? "mt-0" : "mt-1", styles.btnHeight)}>
          <TouchableButton
            style={decrementBtnStyle}
            className={cn("color-white border-0 w-100 justify-content-center align-items-center", "d-flex col-5 p-0", styles.buttons)}
            onClick={handleDecrement}
          >
            {quantity === 1 ? <TrashIcon /> : <MinusIcon />}
          </TouchableButton>
          <div className="w-20 d-flex justify-content-center align-items-center fs-12">
            {quantity}
          </div>
          <TouchableButton
            style={incrementBtnStyle}
            className={cn("color-white border-0 w-100 justify-content-center align-items-center", "d-flex col-5 p-0", styles.buttons)}
            onClick={handleIncrement}
          >
            <PlusIcon />
          </TouchableButton>
        </div>
      ) : null}
    </TouchableDiv>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.tileKey === nextProps.tileKey &&
    prevProps.quantity === nextProps.quantity &&
    prevProps.itemDetails?.outOfStock === nextProps.itemDetails?.outOfStock &&
    prevProps.serverAppTheme?.primaryBtn === nextProps.serverAppTheme?.primaryBtn
  );
});

const CondimentsModalV2 = memo(({
  isOpen,
  serverAppTheme,
  menuItemsHashTable,
  closeCondimentModal,
  seat,
  driveThruLoader,
  finalizeSplit,
  setIsPayClick,
  isPayClick,
  loading,
}) => {
  const condimentsMenu = useMemo(
    () => safeJsonParse(window.sessionStorage.getItem("condimentsMenus"), []),
    [isOpen]
  );

  const [itemQuantity, setItemQuantity] = useState({});

  const handleTileClick = useCallback((tileKey, item, itemDetails) => {
    if ((itemQuantity[tileKey]?.quantity ?? 0) > 0) return;
    setItemQuantity(prev => ({
      ...prev,
      [tileKey]: {
        quantity: 1,
        item: { ...item, menuItem: itemDetails },
      },
    }));
  }, [itemQuantity]);

  const handleIncrement = useCallback((tileKey, item, itemDetails) => {
    setItemQuantity(prev => ({
      ...prev,
      [tileKey]: {
        quantity: (prev[tileKey]?.quantity || 0) + 1,
        item: prev[tileKey]?.item || { ...item, menuItem: itemDetails },
      },
    }));
  }, []);

  const handleDecrement = useCallback((tileKey) => {
    setItemQuantity(prev => {
      const currentQty = prev[tileKey]?.quantity || 0;
      if (currentQty <= 1) {
        const { [tileKey]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [tileKey]: {
          quantity: currentQty - 1,
          item: prev[tileKey]?.item,
        },
      };
    });
  }, []);

  const handlePay = useCallback(() => {
    setIsPayClick(true);
  }, [setIsPayClick]);

  const handleOrderItems = useCallback((e) => {
    e.preventDefault();
    setItemQuantity({});
    closeCondimentModal();
    setIsPayClick(false);
  }, [closeCondimentModal, setIsPayClick]);

  useEffect(() => {
    if (isPayClick && !driveThruLoader) {
      const condimentsToSend = Object.entries(itemQuantity)
        .filter(([, val]) => val.quantity > 0)
        .map(([, { quantity, item }]) => ({
          quantity,
          modifiers: [],
          price: item?.price,
          note: null,
          menuScreenId: item?.menuScreenId,
          menuId: item?.menuId,
          menuItemId: item?.menuItemId,
        }));
      finalizeSplit({ data: seat, condimentItems: condimentsToSend });
    }
  }, [isPayClick, driveThruLoader, seat, itemQuantity, finalizeSplit]);

  const modalStyle = useMemo(() => ({
    maxWidth: "unset",
    backgroundColor: serverAppTheme?.popUp_bg,
    border: "#ffffff",
    boxShadow: serverAppTheme?.popUp_box_shadow,
    margin: "auto",
    width: "100%",
    height: "100%",
    color: serverAppTheme?.primary_text,
  }), [serverAppTheme?.popUp_bg, serverAppTheme?.popUp_box_shadow, serverAppTheme?.primary_text]);

  const orderButtonStyle = useMemo(() => ({
    backgroundColor: serverAppTheme?.secondaryBtn,
    border: serverAppTheme?.cancleBtn_Border,
    color: serverAppTheme?.btn_font_color,
    borderRadius: "8px",
  }), [serverAppTheme?.secondaryBtn, serverAppTheme?.cancleBtn_Border, serverAppTheme?.btn_font_color]);

  const payButtonStyle = useMemo(() => ({
    backgroundColor: serverAppTheme?.enabledPayBtn,
    color: serverAppTheme?.btn_font_color,
    borderRadius: "8px",
    opacity: 1,
  }), [serverAppTheme?.enabledPayBtn, serverAppTheme?.btn_font_color]);

  return (
    <FullWidthModal
      isOpen={isOpen}
      customClass="h-100 w-100"
      customClass2="h-100 w-100"
      style={modalStyle}
    >
      <ModalHeader
        tileHeader={LABEL().CND_HEADER}
        stationName={seat?.stationName}
        seatNumber={seat?.number}
      />

      {driveThruLoader && isPayClick && (
        <Loader message="Waiting for response from pos" />
      )}
      {!driveThruLoader && loading && <Loader />}

      <div className="h-95">
        <div className="h-85 overflowY" style={{ overflowX: "hidden" }}>
          {condimentsMenu.map(({ id, condiments }) =>
            condiments.map(el => (
              <div key={el.id} className="fs_16 fw-600 text-center">
                <div className="ml-2 pt-2">{el.displayName || el?.name}</div>
                <div className="ml-2 pl-1 row h-100">
                  {el.menuItemLinks?.map((item, idx) => {
                    const menuItemId = item.menuItemId;
                    const itemDetails = menuItemsHashTable[menuItemId];
                    const tileKey = `${el.id}_${idx}_${menuItemId}`;
                    const quantity = itemQuantity[tileKey]?.quantity || 0;
                    return (
                      <CondimentTile
                        key={tileKey}
                        tileKey={tileKey}
                        item={item}
                        itemDetails={itemDetails}
                        quantity={quantity}
                        serverAppTheme={serverAppTheme}
                        onTileClick={handleTileClick}
                        onIncrement={handleIncrement}
                        onDecrement={handleDecrement}
                      />
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="col-12 row mx-0 justify-content-center align-items-center position-relative text-center w-100 h-15">
          <div className="col-4 pl-0 pr-1">
            <TouchableButton
              className="btn44 fs_24 fw-600 col-12 px-0"
              type="button"
              onClick={handleOrderItems}
              style={orderButtonStyle}
              rippleEffect
            >
              {LABEL().CND_ORDER_ITEMS}
            </TouchableButton>
          </div>
          <div className="col-4 pl-1 pr-0">
            <TouchableButton
              className="btn44 fs_24 fw-600 col-12 px-0"
              type="button"
              onClick={handlePay}
              rippleEffect
              style={payButtonStyle}
            >
              {LABEL().CND_PAY}
            </TouchableButton>
          </div>
        </div>
      </div>
    </FullWidthModal>
  );
});

export default CondimentsModalV2;
